import { Navigate } from "react-router-dom";
import { RouteObject } from 'react-router-dom';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { GovRouteGate } from '@/components/auth/GovRouteGate';
import { lazyWithRetry } from '@/utils/lazyWithRetry';

// Lazy-loaded protected pages with retry for network reliability
const Auth = lazyWithRetry(() => import('@/pages/Auth'));
const Register = lazyWithRetry(() => import('@/pages/Register'));
const ForgotPassword = lazyWithRetry(() => import('@/pages/ForgotPassword'));
const Onboarding = lazyWithRetry(() => import('@/pages/Onboarding'));
const Trials = lazyWithRetry(() => import('@/pages/Trials'));
const TrialDetail = lazyWithRetry(() => import('@/pages/TrialDetail'));
const PrintableDosingSchedule = lazyWithRetry(() => import('@/pages/PrintableDosingSchedule'));
const Matches = lazyWithRetry(() => import('@/pages/Matches'));
const TrialApplication = lazyWithRetry(() => import('@/pages/TrialApplication'));
const Profile = lazyWithRetry(() => import('@/pages/Profile'));
const Dashboard = lazyWithRetry(() => import('@/pages/Dashboard'));
const SmartRedirect = lazyWithRetry(() => import('@/pages/SmartRedirect'));
const PatientDashboard = lazyWithRetry(() => import('@/pages/PatientDashboard'));
const PatientTrialsInfo = lazyWithRetry(() => import('@/pages/PatientTrialsInfo'));
const CommunityDashboard = lazyWithRetry(() => import('@/pages/CommunityDashboard'));
const HealthPassport = lazyWithRetry(() => import('@/pages/HealthPassport'));
const HealthPassportWeb = lazyWithRetry(() => import('@/pages/HealthPassportWeb'));
const HealthPassportAfrica = lazyWithRetry(() => import('@/pages/HealthPassportAfrica'));
const HealthPassportAccess = lazyWithRetry(() => import('@/pages/HealthPassportAccess'));
const PatientSelfTriage = lazyWithRetry(() => import('@/pages/PatientSelfTriage'));

// Directory management
const AddDirectoryListing = lazyWithRetry(() => import('@/pages/AddDirectoryListing'));
const EditDirectoryListing = lazyWithRetry(() => import('@/pages/EditDirectoryListing'));
const MyListings = lazyWithRetry(() => import('@/pages/MyListings'));

// Trial Site pages - CRITICAL for site staff
const TrialSites = lazyWithRetry(() => import('@/pages/TrialSites'));
const TrialSiteDetail = lazyWithRetry(() => import('@/pages/TrialSiteDetail'));
const TrialSiteECRFTabRedirect = lazyWithRetry(() => import('@/components/redirects/TrialSiteECRFTabRedirect'));
const TrialSiteInvitePatient = lazyWithRetry(() => import('@/pages/TrialSiteInvitePatient'));
const TrialSitePatientRoster = lazyWithRetry(() => import('@/pages/TrialSitePatientRoster'));
const TrialSitePatientDetail = lazyWithRetry(() => import('@/pages/TrialSitePatientDetail'));
const TrialSiteVisitSchedule = lazyWithRetry(() => import('@/pages/TrialSiteVisitSchedule'));
const TrialSiteBulkPatientUpload = lazyWithRetry(() => import('@/pages/TrialSiteBulkPatientUpload'));
const TrialSiteAddPatientManual = lazyWithRetry(() => import('@/pages/TrialSiteAddPatientManual'));
const TrialSiteTrainingVideos = lazyWithRetry(() => import('@/pages/trial-sites/TrialSiteTrainingVideos'));
const TrialSiteImaging = lazyWithRetry(() => import('@/pages/TrialSiteImaging'));
const FieldWorkerDashboard = lazyWithRetry(() => import('@/pages/FieldWorkerDashboard'));
const FieldWorkerLite = lazyWithRetry(() => import('@/pages/trial-site/FieldWorkerLite'));
const SiteECRFRedirect = lazyWithRetry(() => import('@/components/redirects/SiteECRFRedirect'));
const InspectionReadinessOverviewPage = lazyWithRetry(() => import('@/pages/InspectionReadinessOverviewPage'));

// BRIDGE Academy
const AcademyLanding = lazyWithRetry(() => import('@/pages/academy/AcademyLanding'));
const AcademyTierView = lazyWithRetry(() => import('@/pages/academy/AcademyTierView'));
const AcademyModulePlayer = lazyWithRetry(() => import('@/pages/academy/AcademyModulePlayer'));
const AcademyCertificates = lazyWithRetry(() => import('@/pages/academy/AcademyCertificates'));
const AcademyLiveSessions = lazyWithRetry(() => import('@/pages/academy/AcademyLiveSessions'));
const TrainingCohortWorkspace = lazyWithRetry(() => import('@/pages/training/TrainingCohortWorkspace'));
const AcademyVerify = lazyWithRetry(() => import('@/pages/academy/AcademyVerify'));
const AcademyDashboard = lazyWithRetry(() => import('@/pages/academy/AcademyDashboard'));
const TwinNetAcademyEnroll = lazyWithRetry(() => import('@/pages/academy/TwinNetAcademyEnroll'));
const TwinNetAcademyTierView = lazyWithRetry(() => import('@/pages/academy/TwinNetAcademyTierView'));
const AcademyCatalog = lazyWithRetry(() => import('@/pages/academy/AcademyCatalog'));

// PHC Mode (standalone primary health care)
const PHCDashboard = lazyWithRetry(() => import('@/pages/PHCDashboard'));
const PHCPatientRegister = lazyWithRetry(() => import('@/pages/PHCPatientRegister'));
const PHCStats = lazyWithRetry(() => import('@/pages/PHCStats'));
const PHCSupervisorDashboard = lazyWithRetry(() => import('@/pages/PHCSupervisorDashboard'));
const PHCCdstAnalytics = lazyWithRetry(() => import('@/pages/PHCCdstAnalytics'));
const PHCConsultation = lazyWithRetry(() => import('@/pages/PHCConsultation'));
const PHCReferrals = lazyWithRetry(() => import('@/pages/PHCReferrals'));
const PHCSettings = lazyWithRetry(() => import('@/pages/PHCSettings'));
const PHCPrescriptions = lazyWithRetry(() => import('@/pages/PHCPrescriptions'));
const PHCGrowthMonitoring = lazyWithRetry(() => import('@/pages/PHCGrowthMonitoring'));
const PHCImmunizations = lazyWithRetry(() => import('@/pages/PHCImmunizations'));
const PHCAEFIReport = lazyWithRetry(() => import('@/pages/PHCAEFIReport'));
const PHCPostnatalCare = lazyWithRetry(() => import('@/pages/PHCPostnatalCare'));
const PHCNCDManagement = lazyWithRetry(() => import('@/pages/PHCNCDManagement'));
const PHCHepatitisB = lazyWithRetry(() => import('@/pages/PHCHepatitisB'));
const PHCDHIS2Dashboard = lazyWithRetry(() => import('@/pages/PHCDHIS2Dashboard'));
const PHCKnowledgeBase = lazyWithRetry(() => import('@/pages/PHCKnowledgeBase'));
const PHCSelfTriageInbox = lazyWithRetry(() => import('@/pages/PHCSelfTriageInbox'));
const PHCBloodSmear = lazyWithRetry(() => import('@/pages/PHCBloodSmear'));
const PHCHelpCenter = lazyWithRetry(() => import('@/pages/PHCHelpCenter'));
const PHCCHWWorkflow = lazyWithRetry(() => import('@/pages/PHCCHWWorkflow'));
const PHCPrintDoc = lazyWithRetry(() => import('@/pages/PHCPrintDoc'));
const EboAlertsDashboard = lazyWithRetry(() => import('@/pages/vhf/EboAlertsDashboard'));
const EboAlertsAdjudicator = lazyWithRetry(() => import('@/pages/vhf/EboAlertsAdjudicator'));
const EboAlertsAdmin = lazyWithRetry(() => import('@/pages/vhf/EboAlertsAdmin'));
const EboAlertsHandoverBundle = lazyWithRetry(() => import('@/pages/vhf/EboAlertsHandoverBundle'));
const VhfExportAuditLog = lazyWithRetry(() => import('@/pages/vhf/VhfExportAuditLog'));
const Dhis2FieldMappingAdmin = lazyWithRetry(() => import('@/pages/vhf/Dhis2FieldMappingAdmin'));
const EboAlertsHelpCenter = lazyWithRetry(() => import('@/pages/vhf/EboAlertsHelpCenter'));
const GovlinkHelpCenter = lazyWithRetry(() => import('@/pages/gov/GovlinkHelpCenter'));
const M13SovereignExchange = lazyWithRetry(() => import('@/pages/gov/M13SovereignExchange'));
const GovlinkToTCertification = lazyWithRetry(() => import('@/pages/gov/GovlinkToTCertification'));
const EboAlertsEscrowApprovals = lazyWithRetry(() => import('@/pages/vhf/EboAlertsEscrowApprovals'));
const EboAlertsEmailTemplates = lazyWithRetry(() => import('@/pages/vhf/EboAlertsEmailTemplates'));
const ClinicianBloodSmearQueue = lazyWithRetry(() => import('@/pages/ClinicianBloodSmearQueue'));

// Dashboards by role
const SiteDashboard = lazyWithRetry(() => import('@/pages/SiteDashboard'));

// Assessments
const MyAssessments = lazyWithRetry(() => import('@/pages/MyAssessments'));
const PrintableSiteAssessment = lazyWithRetry(() => import('@/pages/PrintableSiteAssessment'));
const SiteAssessmentResults = lazyWithRetry(() => import('@/pages/SiteAssessmentResults'));

// Orders & Matching
const PatientOrderBoard = lazyWithRetry(() => import('@/pages/PatientOrderBoard'));
const CROOrderManagement = lazyWithRetry(() => import('@/pages/CROOrderManagement'));
const PatientMatchPreview = lazyWithRetry(() => import('@/pages/PatientMatchPreview'));
const SponsorPatientRequests = lazyWithRetry(() => import('@/pages/SponsorPatientRequests'));

// Workflows
const BiomarkerSiteMatching = lazyWithRetry(() => import('@/pages/BiomarkerSiteMatching'));
const SponsorSiteSearch = lazyWithRetry(() => import('@/pages/SponsorSiteSearch'));
const OrganizationVerification = lazyWithRetry(() => import('@/pages/OrganizationVerification'));
const UserVerificationDashboard = lazyWithRetry(() => import('@/pages/UserVerificationDashboard'));
const Calendar = lazyWithRetry(() => import('@/pages/Calendar'));
const InviteRegistration = lazyWithRetry(() => import('@/pages/InviteRegistration'));
const SiteInviteRegistration = lazyWithRetry(() => import('@/pages/SiteInviteRegistration'));
const RegistrationThankYou = lazyWithRetry(() => import('@/pages/RegistrationThankYou'));
const ReferralIntake = lazyWithRetry(() => import('@/pages/ReferralIntake'));
const SiteReferralReview = lazyWithRetry(() => import('@/pages/SiteReferralReview'));
const SiteDCTModules = lazyWithRetry(() => import('@/pages/SiteDCTModules'));
const DCTDashboard = lazyWithRetry(() => import('@/pages/DCTDashboard'));
const KnowledgeBase = lazyWithRetry(() => import('@/pages/KnowledgeBase'));
const AdminKnowledgeBaseManagement = lazyWithRetry(() => import('@/pages/AdminKnowledgeBaseManagement'));
const AdminSupportFeedback = lazyWithRetry(() => import('@/pages/AdminSupportFeedback'));
const ECOAUserManual = lazyWithRetry(() => import('@/pages/ECOAUserManual'));
const TrialSiteKnowledgeBase = lazyWithRetry(() => import('@/pages/TrialSiteKnowledgeBase'));
const GovKnowledgeBase = lazyWithRetry(() => import('@/pages/GovKnowledgeBase'));
const SponsorKnowledgeBase = lazyWithRetry(() => import('@/pages/SponsorKnowledgeBase'));
const CROKnowledgeBase = lazyWithRetry(() => import('@/pages/CROKnowledgeBase'));

// Gov surveillance (country-scoped)
const GovCommandCenter = lazyWithRetry(() => import('@/pages/gov/GovCommandCenter'));
const UmojaRegHub = lazyWithRetry(() => import('@/pages/UmojaRegHub'));

const AdminKenyaIDSRMatrix = lazyWithRetry(() => import('@/pages/admin/AdminKenyaIDSRMatrix'));
const AdminIDSRSubmissions = lazyWithRetry(() => import('@/pages/admin/AdminIDSRSubmissions'));
const AdminMEIndicators = lazyWithRetry(() => import('@/pages/admin/AdminMEIndicators'));
const AdminLIMSIDSRLinkages = lazyWithRetry(() => import('@/pages/admin/AdminLIMSIDSRLinkages'));

// BRIDGE — Site Readiness Index + Startup Workspace
const BridgeSiteReadinessIndex = lazyWithRetry(() => import('@/pages/bridge/SiteReadinessIndex'));
const BridgeStartupWorkspace = lazyWithRetry(() => import('@/pages/bridge/StartupWorkspace'));
const BridgeShortlistDetail = lazyWithRetry(() => import('@/pages/bridge/ShortlistDetail'));
const BridgeClaimedSiteProfile = lazyWithRetry(() => import('@/pages/bridge/ClaimedSiteProfile'));

// Lazy features (from LazyRoutes)
import {
  TrialManagementLazy,
  GamificationLazy,
  EducationLazy,
  AIEnhancementsLazy,
  SiteAssessmentLazy,
} from '@/components/layout/LazyRoutes';

// CRITICAL: Direct imports for operationally-critical site pages
// (relocated from admin.routes.tsx — these are site-level, not admin-level)
import SitePatientManagementPro from '@/pages/SitePatientManagementPro';
import SiteActiveTrialsBilling from '@/pages/SiteActiveTrialsBilling';
const BillingVerify = lazyWithRetry(() => import('@/pages/dev/BillingVerify'));
import SiteOMOPDashboard from '@/pages/trial-site/SiteOMOPDashboard';
import SiteDiversityDashboard from '@/pages/trial-site/SiteDiversityDashboard';
import SiteConditionsDashboard from '@/pages/trial-site/SiteConditionsDashboard';
import { ErrorBoundary } from '@/components/optimization/ErrorBoundary';

/**
 * Protected routes requiring authentication
 */
export const protectedRoutes: RouteObject[] = [
  // Auth routes (technically not protected but related to auth flow)
  { path: '/auth', element: <Auth /> },
  { path: '/register', element: <Register /> },
  { path: '/forgot-password', element: <ForgotPassword /> },
  { path: '/invite-registration', element: <InviteRegistration /> },
  { path: '/register/site/:siteSlug', element: <SiteInviteRegistration /> },
  { path: '/registration-thank-you', element: <RegistrationThankYou /> },
  
  // Onboarding
  { path: '/onboarding', element: <ProtectedRoute><Onboarding /></ProtectedRoute> },
  
  // Patient features
  { path: '/trials', element: <Trials /> },
  { path: '/trials/:id', element: <TrialDetail /> },
  { path: '/trials/:trialId/dosing/print', element: <PrintableDosingSchedule /> },
  { path: '/matches', element: <Matches /> },
  { path: '/apply-to-trial', element: <ProtectedRoute><TrialApplication /></ProtectedRoute> },
  { path: '/profile', element: <ProtectedRoute><Profile /></ProtectedRoute> },
  { path: '/dashboard', element: <Dashboard /> },
  { path: '/patient-dashboard', element: <PatientDashboard /> },
  { path: '/patient-trials-info', element: <PatientTrialsInfo /> },
  { path: '/community-dashboard', element: <CommunityDashboard /> },
  { path: '/health-passport', element: <ProtectedRoute><HealthPassport /></ProtectedRoute> },
  { path: '/health-passport-africa', element: <HealthPassportAfrica /> },
  { path: '/my-health-passport', element: <ProtectedRoute><HealthPassport /></ProtectedRoute> },
  { path: '/health-passport/web', element: <ProtectedRoute><HealthPassportWeb /></ProtectedRoute> },
  { path: '/health-passport/access/:shareCode?', element: <HealthPassportAccess /> },
  { path: '/my-health-passport/check-symptoms', element: <PatientSelfTriage /> },
  { path: '/check-symptoms', element: <PatientSelfTriage /> },
  { path: '/progress', element: <ProtectedRoute><GamificationLazy /></ProtectedRoute> },
  { path: '/education', element: <ProtectedRoute><EducationLazy /></ProtectedRoute> },
  
  // Directory management
  { path: '/directory/add', element: <AddDirectoryListing /> },
  { path: '/directory/listing/:id/edit', element: <ProtectedRoute><EditDirectoryListing /></ProtectedRoute> },
  { path: '/profile/listings', element: <ProtectedRoute><MyListings /></ProtectedRoute> },
  
  // Trial site management
  { path: '/trial-sites/new', element: <TrialSites /> },
  { path: '/trial-site', element: <Navigate to="/site-dashboard" replace /> },
  { path: '/trial-site/:id', element: <ProtectedRoute><TrialSiteDetail /></ProtectedRoute> },
  { path: '/trial-site/invite-patient', element: <ProtectedRoute><TrialSiteInvitePatient /></ProtectedRoute> },
  { path: '/trial-site/bulk-upload', element: <ProtectedRoute><TrialSiteBulkPatientUpload /></ProtectedRoute> },
  { path: '/trial-site/:siteId/patients', element: <ProtectedRoute><TrialSitePatientRoster /></ProtectedRoute> },
  { path: '/trial-site/:siteId/add-patient-manual', element: <ProtectedRoute><TrialSiteAddPatientManual /></ProtectedRoute> },
  { path: '/trial-site/:siteId/patient/:patientId', element: <ProtectedRoute><TrialSitePatientDetail /></ProtectedRoute> },
  { path: '/trial-site/:siteId/visits', element: <ProtectedRoute><TrialSiteVisitSchedule /></ProtectedRoute> },
  { path: '/trial-site/:siteId/ecrf', element: <ProtectedRoute><TrialSiteECRFTabRedirect /></ProtectedRoute> },
  { path: '/trial-site/:siteId/imaging', element: <ProtectedRoute><TrialSiteImaging /></ProtectedRoute> },
  { path: '/trial-site/:siteId/field-worker', element: <ProtectedRoute><FieldWorkerDashboard /></ProtectedRoute> },
  { path: '/trial-site/:siteId/field-worker-lite', element: <ProtectedRoute><FieldWorkerLite /></ProtectedRoute> },
  { path: '/inspection-readiness', element: <ProtectedRoute><InspectionReadinessOverviewPage /></ProtectedRoute> },
  
  // Site-specific operational pages (relocated from admin.routes.tsx)
  { path: '/trial-site/:siteId/patient-management', element: <ProtectedRoute><ErrorBoundary><SitePatientManagementPro /></ErrorBoundary></ProtectedRoute> },
  { path: '/trial-site/:siteId/billing', element: <ProtectedRoute><ErrorBoundary><SiteActiveTrialsBilling /></ErrorBoundary></ProtectedRoute> },
  { path: '/dev/billing-verify', element: <ProtectedRoute><BillingVerify /></ProtectedRoute> },
  { path: '/trial-site/:siteId/omop', element: <ProtectedRoute><ErrorBoundary><SiteOMOPDashboard /></ErrorBoundary></ProtectedRoute> },
  { path: '/trial-site/:siteId/diversity', element: <ProtectedRoute><ErrorBoundary><SiteDiversityDashboard /></ErrorBoundary></ProtectedRoute> },
  { path: '/trial-site/:siteId/conditions', element: <ProtectedRoute><ErrorBoundary><SiteConditionsDashboard /></ErrorBoundary></ProtectedRoute> },
  
  // PHC Mode (standalone primary health care — no trial site context)
  { path: '/phc', element: <ProtectedRoute><PHCDashboard /></ProtectedRoute> },
  { path: '/phc/register', element: <ProtectedRoute><PHCPatientRegister /></ProtectedRoute> },
  { path: '/phc/consultation', element: <ProtectedRoute><PHCConsultation /></ProtectedRoute> },
  { path: '/phc/referrals', element: <ProtectedRoute><PHCReferrals /></ProtectedRoute> },
  { path: '/phc/stats', element: <ProtectedRoute><PHCStats /></ProtectedRoute> },
  { path: '/phc/supervisor', element: <ProtectedRoute><PHCSupervisorDashboard /></ProtectedRoute> },
  { path: '/phc/cdst-analytics', element: <ProtectedRoute><PHCCdstAnalytics /></ProtectedRoute> },
  { path: '/phc/settings', element: <ProtectedRoute><PHCSettings /></ProtectedRoute> },
  { path: '/phc/prescriptions', element: <ProtectedRoute><PHCPrescriptions /></ProtectedRoute> },
  { path: '/phc/growth', element: <ProtectedRoute><PHCGrowthMonitoring /></ProtectedRoute> },
  { path: '/phc/immunizations', element: <ProtectedRoute><PHCImmunizations /></ProtectedRoute> },
  { path: '/phc/aefi/report', element: <ProtectedRoute><PHCAEFIReport /></ProtectedRoute> },
  { path: '/phc/pnc', element: <ProtectedRoute><PHCPostnatalCare /></ProtectedRoute> },
  { path: '/phc/ncd', element: <ProtectedRoute><PHCNCDManagement /></ProtectedRoute> },
  { path: '/phc/hepatitis-b', element: <ProtectedRoute><PHCHepatitisB /></ProtectedRoute> },
  { path: '/phc/dhis2-dashboard', element: <ProtectedRoute><PHCDHIS2Dashboard /></ProtectedRoute> },
  { path: '/phc/knowledge-base', element: <ProtectedRoute><PHCKnowledgeBase /></ProtectedRoute> },
  { path: '/phc/self-triage-inbox', element: <ProtectedRoute><PHCSelfTriageInbox /></ProtectedRoute> },
  { path: '/phc/blood-smear', element: <ProtectedRoute><PHCBloodSmear /></ProtectedRoute> },
  { path: '/phc/help', element: <ProtectedRoute><PHCHelpCenter /></ProtectedRoute> },
  { path: '/phc/chw-workflow', element: <ProtectedRoute><PHCCHWWorkflow /></ProtectedRoute> },
  { path: '/phc/print', element: <ProtectedRoute><PHCPrintDoc /></ProtectedRoute> },
  { path: '/phc/print/:docId', element: <ProtectedRoute><PHCPrintDoc /></ProtectedRoute> },
  { path: '/vhf/ebo-alerts', element: <ProtectedRoute requireModule="ebo-alerts"><EboAlertsDashboard /></ProtectedRoute> },
  { path: '/vhf/adjudicator', element: <ProtectedRoute requireModule="ebo-alerts"><EboAlertsAdjudicator /></ProtectedRoute> },
  { path: '/vhf/admin', element: <ProtectedRoute requireModule="ebo-alerts"><EboAlertsAdmin /></ProtectedRoute> },
  { path: '/vhf/handover-bundle', element: <ProtectedRoute requireModule="ebo-alerts"><EboAlertsHandoverBundle /></ProtectedRoute> },
  { path: '/vhf/export-audit', element: <ProtectedRoute requireModule="ebo-alerts"><VhfExportAuditLog /></ProtectedRoute> },
  { path: '/vhf/dhis2-mapping', element: <ProtectedRoute requireModule="ebo-alerts"><Dhis2FieldMappingAdmin /></ProtectedRoute> },
  { path: '/vhf/help', element: <ProtectedRoute requireModule="ebo-alerts"><EboAlertsHelpCenter /></ProtectedRoute> },
  { path: '/vhf/escrow-approvals', element: <ProtectedRoute requireModule="ebo-alerts"><EboAlertsEscrowApprovals /></ProtectedRoute> },
  { path: '/vhf/escrow-email-templates', element: <ProtectedRoute requireModule="ebo-alerts"><EboAlertsEmailTemplates /></ProtectedRoute> },

  // Govlink help center — user manual + SOP library (any signed-in gov/NRA user)
  { path: '/gov/help', element: <ProtectedRoute><GovlinkHelpCenter /></ProtectedRoute> },

  // Govlink training — M13 Sovereign Exchange + Train-the-Trainer certification
  { path: '/gov/training/m13', element: <ProtectedRoute><M13SovereignExchange /></ProtectedRoute> },
  { path: '/gov/training/tot', element: <ProtectedRoute><GovlinkToTCertification /></ProtectedRoute> },

  // Gov surveillance — country-scoped operational surfaces (gov/platform-admin only)
  { path: '/gov/:country/command-center', element: <ProtectedRoute><GovRouteGate><GovCommandCenter /></GovRouteGate></ProtectedRoute> },
  { path: '/umojareg/hub', element: <ProtectedRoute><UmojaRegHub /></ProtectedRoute> },

  { path: '/gov/:country/idsr-matrix', element: <ProtectedRoute><GovRouteGate><AdminKenyaIDSRMatrix /></GovRouteGate></ProtectedRoute> },
  { path: '/gov/:country/idsr-submissions', element: <ProtectedRoute><GovRouteGate><AdminIDSRSubmissions /></GovRouteGate></ProtectedRoute> },
  { path: '/gov/:country/me-indicators', element: <ProtectedRoute><GovRouteGate><AdminMEIndicators /></GovRouteGate></ProtectedRoute> },
  { path: '/gov/:country/lims-idsr-linkages', element: <ProtectedRoute><GovRouteGate><AdminLIMSIDSRLinkages /></GovRouteGate></ProtectedRoute> },

  { path: '/clinician/blood-smear-queue', element: <ProtectedRoute><ClinicianBloodSmearQueue /></ProtectedRoute> },
  
  // Trial Site Training Videos
  { path: '/trial-site/training-videos', element: <ProtectedRoute><TrialSiteTrainingVideos /></ProtectedRoute> },
  
  // Site eCRF (role-based route - redirects to user's site)
  { path: '/site-ecrf', element: <ProtectedRoute><SiteECRFRedirect /></ProtectedRoute> },
  
  // Observer Portal moved to public.routes.tsx for unauthenticated access
  
  // Dashboards
  { path: '/site-dashboard', element: <SiteDashboard /> },
  
  // Trial management
  { path: '/trial-management', element: <ProtectedRoute><TrialManagementLazy /></ProtectedRoute> },
  { path: '/ai-enhancements', element: <AIEnhancementsLazy /> },
  
  // Assessments
  { path: '/site-assessment', element: <SiteAssessmentLazy /> },
  { path: '/site-assessment/print', element: <ProtectedRoute><PrintableSiteAssessment /></ProtectedRoute> },
  { path: '/my-assessments', element: <ProtectedRoute><MyAssessments /></ProtectedRoute> },
  { path: '/site-assessment-results', element: <SiteAssessmentResults /> },
  
  // Orders & matching
  { path: '/patient-orders', element: <ProtectedRoute><PatientOrderBoard /></ProtectedRoute> },
  { path: '/cro-orders', element: <ProtectedRoute><CROOrderManagement /></ProtectedRoute> },
  { path: '/patient-match-preview', element: <ProtectedRoute><PatientMatchPreview /></ProtectedRoute> },
  { path: '/sponsor-patient-requests', element: <SponsorPatientRequests /> },
  
  // Workflows
  { path: '/biomarker-site-matching', element: <BiomarkerSiteMatching /> },
  { path: '/sponsor-site-search', element: <SponsorSiteSearch /> },
  { path: '/organization-verification', element: <OrganizationVerification /> },
  { path: '/user-verification', element: <UserVerificationDashboard /> },
  { path: '/calendar', element: <Calendar /> },
  { path: '/referrals/intake', element: <ReferralIntake /> },
  { path: '/referrals/review', element: <SiteReferralReview /> },
  { path: '/sites/:siteId/modules', element: <ProtectedRoute><ErrorBoundary><SiteDCTModules /></ErrorBoundary></ProtectedRoute> },
  { path: '/dct-dashboard', element: <DCTDashboard /> },
  { path: '/knowledge-base', element: <ProtectedRoute><KnowledgeBase /></ProtectedRoute> },
  { path: '/admin/knowledge-base', element: <ProtectedRoute><AdminKnowledgeBaseManagement /></ProtectedRoute> },
  { path: '/admin/support-feedback', element: <ProtectedRoute><AdminSupportFeedback /></ProtectedRoute> },
  { path: '/trial-site/:siteId/knowledge-base', element: <ProtectedRoute><TrialSiteKnowledgeBase /></ProtectedRoute> },
  { path: '/gov/knowledge-base/:token', element: <GovKnowledgeBase /> },
  { path: '/sponsor/knowledge-base', element: <ProtectedRoute><SponsorKnowledgeBase /></ProtectedRoute> },
  { path: '/cro/knowledge-base', element: <ProtectedRoute><CROKnowledgeBase /></ProtectedRoute> },
  // eCOA Manual moved to public.routes.tsx for unauthenticated access

  // BRIDGE — Site Readiness Index + unified Startup Workspace + persisted shortlists
  { path: '/bridge/site-readiness', element: <ProtectedRoute><BridgeSiteReadinessIndex /></ProtectedRoute> },
  { path: '/bridge/startup-workspace', element: <ProtectedRoute><BridgeStartupWorkspace /></ProtectedRoute> },
  { path: '/bridge/shortlists/:id', element: <ProtectedRoute><BridgeShortlistDetail /></ProtectedRoute> },
  { path: '/bridge/sites/:trialSiteId/profile', element: <ProtectedRoute><BridgeClaimedSiteProfile /></ProtectedRoute> },

  // BRIDGE Academy — tiered certification curriculum
  { path: '/academy/catalog', element: <AcademyCatalog /> },
  { path: '/academy', element: <ProtectedRoute><AcademyLanding /></ProtectedRoute> },
  { path: '/academy/dashboard', element: <ProtectedRoute><AcademyDashboard /></ProtectedRoute> },
  { path: '/academy/certificates', element: <ProtectedRoute><AcademyCertificates /></ProtectedRoute> },
  { path: '/academy/live-sessions', element: <ProtectedRoute><AcademyLiveSessions /></ProtectedRoute> },
  { path: '/academy/verify/:certId', element: <AcademyVerify /> },
  { path: '/academy/module/:slug', element: <ProtectedRoute><AcademyModulePlayer /></ProtectedRoute> },

  // TwinNet Academy — must be declared BEFORE the catch-all /academy/:tier route below
  { path: '/academy/twinnet', element: <ProtectedRoute><TwinNetAcademyEnroll /></ProtectedRoute> },
  { path: '/academy/twinnet/:tier', element: <ProtectedRoute><TwinNetAcademyTierView /></ProtectedRoute> },

  { path: '/academy/:tier', element: <ProtectedRoute><AcademyTierView /></ProtectedRoute> },

  // CRA training sandbox (isolated cohort tenants, synthetic data only)
  { path: '/training/cohort/:cohortId', element: <ProtectedRoute><TrainingCohortWorkspace /></ProtectedRoute> },
];

// Export lazy components for prefetching
export const protectedRoutePrefetch = {
  Trials,
  TrialDetail,
  Profile,
  Dashboard,
  PatientDashboard,
};
